import React, { useState } from "react";
import DatePicker, { registerLocale, CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, endOfDay, format, isValid, startOfDay, subDays } from 'date-fns';
import { makeStyles } from '@material-ui/core';
import es from 'date-fns/locale/es'
import { parseDateStrDashToLocalDate } from "../../helpers/common";
import { Button, Dialog, Box, IconButton } from '@material-ui/core';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import useDeviceDetect from "../../hooks/useDeviceDetect";
import CancelIcon from '@mui/icons-material/Cancel';

const useStyles = makeStyles(theme => ({
    dateContainer: {
        margin: "0 2px 0 2px",
        borderStyle: "none",
        '& .react-datepicker__input-container': {
            '& input': {
                width: "100%",
                cursor: "pointer",
                margin: "auto",
                height: "35px",
                fontSize: "15px",
                font: "inherit",
                textAlign: "center",
                color: "#5e5b5b",
                borderColor: "#e2e2e2",
                borderWidth: "1px",
                borderStyle: "none"
            },
        },
        '& .react-datepicker-popper': {
            width: "100%"
        },
        '& .react-datepicker': {
            borderStyle: "none"
        },
        '& .react-datepicker__day-name': {
            color: "#827a7a"
        },
        '& .react-datepicker__day--in-selecting-range': {
            backgroundColor: "#216ba5ad"
        },
        '& .react-datepicker__day': {
            borderRadius: "50% !important",
        },
        '& .react-datepicker__day--today': {
            color: "#88c0ed",
        },
        '& .react-datepicker__header': {
            backgroundColor: "#f4f4f5",
        },
        '& .react-datepicker__month-container': {
            padding: "5px",
            height: "315px"
        },
        '& .react-datepicker__navigation--previous ': {
            top: "10px",
            right: "25px"
        },
        '& .react-datepicker__navigation--next ': {
            top: "10px",

        },
        '& .react-datepicker__day--disabled': {
            color: "#ec222254"
        },
    },
    btn: {
        margin: theme.spacing(1, 1),
        fontSize: "13px",
        letterSpacing: "2px",
        minHeight: "37px"
    },
    iconDate: {
        marginLeft: "10px",

    },
    buttonReady: {
        zIndex: "2",
        backgroundColor: "#f4f4f5",
        marginBottom: "8px"
    },
    buttonClose: {
        width: '40px',
    }
}));

const DatePickerBill = ({
    setActiveStep, datePickerDisabled,
    setEndDate, endDate, startDate, setStartDate,
    displayDisabled, setBillData, billData
}) => {

    const { isMobile } = useDeviceDetect();
    const classes = useStyles();
    const [maxDate, setMaxDate] = useState(new Date())
    const [isOpen, setIsOpen] = useState(false);

    registerLocale("es", es)

    const getDisabledIntervals = () => {
        let arryRes = []
        datePickerDisabled?.no_disponibles.forEach(element => {
            const from = parseDateStrDashToLocalDate(element.desde)
            const to = parseDateStrDashToLocalDate(element.hasta)

            if (from && to) {
                let obj = { start: startOfDay(from), end: endOfDay(to) }
                arryRes.push(obj)
            }
        });

        const orderedIntervals = arryRes.sort((a, b) => a.start - b.start)
        const normalizedIntervals = []

        orderedIntervals.forEach(interval => {
            const lastInterval = normalizedIntervals[normalizedIntervals.length - 1]

            if (!lastInterval) {
                normalizedIntervals.push(interval)
                return
            }

            // Si backend deja un hueco de 1 dia entre bloques no disponibles,
            // lo cerramos para evitar dias "sueltos" que rompen la seleccion.
            if (startOfDay(interval.start) <= addDays(startOfDay(lastInterval.end), 2)) {
                lastInterval.end = interval.end > lastInterval.end ? interval.end : lastInterval.end
                return
            }

            normalizedIntervals.push(interval)
        })

        return normalizedIntervals
    }

    const setMax = (start, end) => {
        setMaxDate(new Date())
        let startCopy = start ? new Date(start) : null;

        if (startCopy) {
            const disabledIntervals = getDisabledIntervals()

            for (let i = 0; i < disabledIntervals.length; i++) {
                let element = disabledIntervals[i]
                if (startCopy < element.start) {
                    setMaxDate(subDays(element.start, 1))
                    break
                }
            }
        }

        if (end) {
            setMaxDate(new Date())
        }
    }

    const onChanges = (e) => {
        const [start, end] = e;
        setMax(start, end)
        setStartDate(start)
        setEndDate(end);
        if (end !== null) {
            setActiveStep(3)

            if (setBillData && billData) {
                setBillData(billData => ({
                    ...billData,
                    date_from: format(start, 'yyyy-MM-dd'),
                    date_to: format(end, 'yyyy-MM-dd')
                }));
            }
        }
    };

    const MyContainer = ({ className, children }) => {
        return (
            <CalendarContainer className={className}>
                <Box
                    style={{
                        width: "100%",
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}>

                    <IconButton
                        className={classes.buttonClose}
                        color='primary'
                        onClick={() => setIsOpen(false)}
                    >
                        <CancelIcon />
                    </IconButton>
                </Box>


                <Box style={{ position: "relative" }}>
                    {children}
                </Box>

                <Box
                    style={{
                        width: "100%",
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                    <Button
                        variant='contained'
                        className={classes.buttonReady}
                        size="small"
                        aria-label="upload picture"
                        onClick={() => setIsOpen(false)} >
                        Aceptar
                    </Button>
                </Box>
            </CalendarContainer>
        );
    };

    const handleClick = (e) => {
        e.preventDefault();
        setIsOpen(!isOpen);
    };

    const hasValidStartDate = isValid(startDate);
    const hasValidEndDate = isValid(endDate);

    return (
        <>
            <Dialog
                open={isOpen}
                aria-labelledby="form-dialog-title"
            >
                <Box className={classes.dateContainer}>

                    <DatePicker
                        dateFormat="dd/MM/yyyy"
                        onClickOutside={() => setIsOpen(false)}
                        open={isOpen}
                        selected={hasValidStartDate ? startDate : null}
                        locale="es"
                        startDate={hasValidStartDate ? startDate : null}
                        endDate={hasValidEndDate ? endDate : null}
                        onChange={onChanges}
                        monthsShown={isMobile ? 1 : 2}
                        selectsRange
                        disabled={displayDisabled}
                        maxDate={maxDate}
                        excludeDateIntervals={getDisabledIntervals()}
                        disabledKeyboardNavigation
                        inline
                        calendarContainer={MyContainer}
                    />

                </Box>
            </Dialog>

            <Box style={{ fontSize: "16px" }} >
                {hasValidStartDate && `${format(startDate, "dd-MM-yyyy")}   /  ${hasValidEndDate ? format(endDate, "dd-MM-yyyy") : ''}`}
            </Box>

            <Box>
                <Button type='submit' disabled={displayDisabled} onClick={handleClick} color='primary' variant='contained' className={classes.btn}>
                    {'Seleccione periodo'}
                    {!isMobile && <InsertInvitationIcon className={classes.iconDate} />}
                </Button>
            </Box>
        </>
    );
}

export default DatePickerBill
